import React from 'react';
import { Link } from 'react-router-dom';
import { MissingData, MissingMatch } from '../../utils/majorAnalysis';

// What the analysis still needs, said plainly, with a button to the exact
// place to add it: hole pars on Courses, and scores / putts / penalties in
// the round's By Hole editor (typed in, or from a scorecard photo).

export const shortDate = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
};

const names = (players: MissingMatch['players'], kind?: string) =>
  players.filter((p) => !kind || p.kind === kind).map((p) => p.name.split(' ')[0]);

const OpenCourses: React.FC = () => (
  <Link to="/courses" className="ma-btn ma-btn--link">Open Courses →</Link>
);

export const ParsAsk: React.FC<{ missing: MissingData; only?: string[] }> = ({ missing, only }) => {
  const without = missing.teeboxesWithoutPars.filter((t) => !only || only.includes(t.courseKey));
  const partial = missing.teeboxesPartialPars.filter((t) => !only || only.includes(t.courseKey));
  if (!without.length && !partial.length) return null;
  return (
    <div className="ma-ask">
      <div className="ma-ask-head">
        <span className="ma-ask-title">Add par for each hole</span>
        <OpenCourses />
      </div>
      <ul className="ma-ask-list">
        {without.map((t) => (
          <li key={t.courseKey}>
            <strong>{t.label}</strong>{t.par != null ? ` (par ${t.par})` : ''} has no hole pars · used by {t.rounds} round{t.rounds === 1 ? '' : 's'}
          </li>
        ))}
        {partial.map((t) => (
          <li key={t.courseKey}>
            <strong>{t.label}</strong>: hole{t.missingHoles.length === 1 ? '' : 's'} {t.missingHoles.join(', ')} {t.missingHoles.length === 1 ? 'has' : 'have'} no par
          </li>
        ))}
      </ul>
      <p className="ma-ask-hint">
        On Courses, open the tee box and pick each hole's par (or let the tee box agent fill them in).
        Unlocks birdies/pars/bogeys, everything vs par, exact mid-round places, GIR and scrambling.
      </p>
    </div>
  );
};

export const HolesAsk: React.FC<{ matches: MissingMatch[]; onOpenRound: (roundId: number) => void; unlocks?: string }> = ({ matches, onOpenRound, unlocks }) => {
  if (!matches.length) return null;
  return (
    <div className="ma-ask">
      <div className="ma-ask-head"><span className="ma-ask-title">Add hole-by-hole scores</span></div>
      <ul className="ma-ask-list">
        {matches.map((m) => {
          const missing = names(m.players, 'missing');
          const partial = m.players.filter((p) => p.kind === 'partial');
          const mismatch = m.players.filter((p) => p.kind === 'mismatch');
          return (
            <li key={m.round_id} className="ma-ask-row">
              <span>
                <strong>{m.course}</strong> · {shortDate(m.date)}
                {missing.length > 0 && <> — missing {missing.join(', ')}</>}
                {partial.length > 0 && <> — partial: {partial.map((p) => `${p.name.split(' ')[0]} (${p.detail})`).join(', ')}</>}
                {mismatch.length > 0 && <> — doesn’t add up: {mismatch.map((p) => `${p.name.split(' ')[0]} (${p.detail})`).join(', ')}</>}
              </span>
              <button type="button" className="ma-btn" onClick={() => onOpenRound(m.round_id)}>Add scores</button>
            </li>
          );
        })}
      </ul>
      <p className="ma-ask-hint">
        Type them in, or use 📷 Upload scorecard in the round’s By Hole view.
        {unlocks ? ` Unlocks ${unlocks}.` : ''}
      </p>
    </div>
  );
};

export const PuttsPenaltiesAsk: React.FC<{
  putts: MissingMatch[];
  penalties: MissingMatch[];
  onOpenRound: (roundId: number) => void;
  show?: 'both' | 'putts' | 'penalties';
}> = ({ putts, penalties, onOpenRound, show = 'both' }) => {
  const byRound = new Map<number, { m: MissingMatch; putts: string[]; pens: string[] }>();
  if (show !== 'penalties') putts.forEach((m) => byRound.set(m.round_id, { m, putts: names(m.players), pens: [] }));
  if (show !== 'putts') {
    penalties.forEach((m) => {
      const e = byRound.get(m.round_id) ?? { m, putts: [], pens: [] };
      e.pens = names(m.players);
      byRound.set(m.round_id, e);
    });
  }
  const rows = Array.from(byRound.values()).sort((a, b) => a.m.date.localeCompare(b.m.date) || a.m.round_id - b.m.round_id);
  if (!rows.length) return null;
  const title = show === 'putts' ? 'Add putts per hole' : show === 'penalties' ? 'Add penalty strokes per hole' : 'Add putts and penalties per hole';
  return (
    <div className="ma-ask">
      <div className="ma-ask-head"><span className="ma-ask-title">{title}</span></div>
      <ul className="ma-ask-list">
        {rows.map(({ m, putts: p, pens }) => (
          <li key={m.round_id} className="ma-ask-row">
            <span>
              <strong>{m.course}</strong> · {shortDate(m.date)}
              {p.length > 0 && <> — no putts entered for {p.join(', ')}</>}
              {pens.length > 0 && <> — no penalty strokes entered for {pens.join(', ')}</>}
            </span>
            <button type="button" className="ma-btn" onClick={() => onOpenRound(m.round_id)}>Add putts &amp; penalties</button>
          </li>
        ))}
      </ul>
      <p className="ma-ask-hint">
        In the round’s By Hole view, Edit → putts and penalties are per-hole fields (or switch them on when reviewing a scorecard photo).
        {show !== 'putts' && ' If there were no penalties, there’s nothing to add — a blank penalty can’t be told apart from a clean hole.'}
      </p>
    </div>
  );
};

/** The panel at the top of the Analysis: counts, then the three asks. */
const DataNeeded: React.FC<{
  missing: MissingData;
  onOpenRound: (roundId: number) => void;
  open: boolean;
  onToggle: () => void;
}> = ({ missing, onOpenRound, open, onToggle }) => {
  if (!missing.anything) return null;
  const c = missing.counts;
  const chip = (label: string, ok: number, total: number, unit: string) => (
    <span className={`ma-count${ok === total ? ' is-done' : ''}`}>
      {label} <strong>{ok} of {total}</strong> {unit}
    </span>
  );
  return (
    <section className="ma-needed" aria-label="Data needed">
      <button type="button" className="ma-needed-toggle" aria-expanded={open} onClick={onToggle}>
        <span className="ma-needed-title">Data needed for the full analysis</span>
        <span className="ma-needed-counts">
          {chip('Hole pars', c.teeboxes.ok, c.teeboxes.total, 'tee boxes')}
          {chip('Hole scores', c.holes.ok, c.holes.total, 'rounds')}
          {chip('Putts', c.putts.ok, c.putts.total, 'rounds')}
          {chip('Penalties', c.penalties.ok, c.penalties.total, 'rounds')}
        </span>
        <span className="ma-needed-caret" aria-hidden>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="ma-needed-body">
          <ParsAsk missing={missing} />
          <HolesAsk matches={missing.holeScores} onOpenRound={onOpenRound} unlocks="the hole-by-hole position trend, head-to-head holes, hole by hole and scoring" />
          <PuttsPenaltiesAsk putts={missing.putts} penalties={missing.penalties} onOpenRound={onOpenRound} />
        </div>
      )}
    </section>
  );
};

export default DataNeeded;
